import React, { useState, useCallback } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { guidanceAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

export default function WatchButton({ entityType, entityId, size = 'sm' }) {
  const { user } = useAuth();
  const [watching, setWatching] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check initial state
  React.useEffect(() => {
    if (!user) return;
    guidanceAPI.checkWatching(entityType, entityId)
      .then(res => setWatching(res.data?.watching || false))
      .catch(() => setWatching(false));
  }, [user, entityType, entityId]);

  const toggle = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { toast.error('Sign in to watch'); return; }
    setLoading(true);
    try {
      if (watching) {
        await guidanceAPI.unwatch(entityType, entityId);
        setWatching(false);
        toast.success('Removed from watchlist');
      } else {
        await guidanceAPI.watch(entityType, entityId);
        setWatching(true);
        toast.success('Added to watchlist');
      }
    } catch { toast.error('Failed to update watchlist'); }
    finally { setLoading(false); }
  }, [user, watching, entityType, entityId]);

  if (!user || watching === null) return null;

  const isSmall = size === 'sm';

  return (
    <button
      onClick={toggle}
      disabled={loading}
      className={`inline-flex items-center gap-1.5 font-medium transition-all ${
        watching
          ? 'text-[#b8ff00]'
          : 'text-zinc-500 hover:text-zinc-300'
      } ${isSmall ? 'text-xs px-2 py-1' : 'text-sm px-3 py-1.5 border border-zinc-700 rounded-lg hover:border-zinc-500'}`}
      data-testid={`watch-btn-${entityType}-${entityId}`}
    >
      {watching ? <EyeOff size={isSmall ? 12 : 14} /> : <Eye size={isSmall ? 12 : 14} />}
      {watching ? 'Watching' : 'Watch'}
    </button>
  );
}
